/** Sculpt fields in natural rest space, shared by the body and every garment.
 * Identical positions receive identical deltas across material/face seams.
 * Joint centres, topology, skin weights and expression targets are preserved.
 */
import * as THREE from 'three';
import { BODY_SHAPE_AXES } from '@hyperlight/shared';

export function bodyShapeMorphs(pos: Float32Array, rig: Map<string, THREE.Matrix4>, _body: 'male' | 'female', _normals: Float32Array) {
  const point = (name: string) => new THREE.Vector3().setFromMatrixPosition(rig.get(name)!);
  const hip = point('pelvis'), neck = point('neck_01');
  const torso = neck.y - hip.y;
  const bell = (v: number, centre: number, radius: number) => {
    const t = Math.max(0, 1 - ((v - centre) / radius) ** 2);
    return t * t;
  };
  const limbSegments = ['l', 'r'].flatMap(side => [
    [point(`upperarm_${side}`), point(`lowerarm_${side}`), 0.032],
    [point(`lowerarm_${side}`), point(`hand_${side}`), 0.018],
    [point(`thigh_${side}`), point(`calf_${side}`), 0.04],
    [point(`calf_${side}`), point(`foot_${side}`), 0.025],
  ] as [THREE.Vector3, THREE.Vector3, number][]);
  return Object.keys(BODY_SHAPE_AXES).filter(k => k !== 'stature').map(axis => {
    const delta = new Float32Array(pos.length);
    for (let i = 0; i < pos.length; i += 3) {
      const x = pos[i], y = pos[i + 1], z = pos[i + 2];
      const central = bell(x, 0, 0.34);
      const waist = bell(y, hip.y + torso * 0.3, torso * 0.55) * central;
      const chest = bell(y, hip.y + torso * 0.77, torso * 0.43) * central;
      const hips = bell(y, hip.y - 0.055, 0.22) * bell(x, 0, 0.4);
      let dx = 0, dy = 0, dz = 0;
      if (axis === 'mass') {
        const w = waist * 0.42 + chest * 0.18 + hips * 0.22;
        dx = x * w; dz = (z - hip.z) * w;
        dz += waist * 0.028;
      } else if (axis === 'waist') {
        dx = x * waist * 0.26; dz = (z - hip.z) * waist * 0.27;
      } else if (axis === 'hips') {
        dx = x * hips * 0.27; dz = (z - hip.z) * hips * 0.18;
      } else if (axis === 'chest') {
        dx = x * chest * 0.15; dz = (z - hip.z) * chest * 0.3;
      } else if (axis === 'muscle') {
        dx = x * chest * 0.16 - x * waist * 0.06;
        dz = (z - hip.z) * chest * 0.14;
      }
      if (axis === 'muscle' || axis === 'mass') {
        // Smooth radial growth around each long bone; taper to zero at joints.
        for (const [a, b, amount] of limbSegments) {
          const vx = b.x-a.x, vy = b.y-a.y, vz = b.z-a.z;
          const t = ((x-a.x)*vx + (y-a.y)*vy + (z-a.z)*vz)/(vx*vx+vy*vy+vz*vz);
          if (t <= 0 || t >= 1) continue;
          const rx = x-a.x-t*vx, ry = y-a.y-t*vy, rz = z-a.z-t*vz;
          const r = Math.hypot(rx, ry, rz);
          const w = bell(t, 0.48, 0.5) * bell(r, 0, 0.24) * amount * (axis === 'mass' ? 0.65 : 1) / Math.max(r, 0.04);
          dx += rx*w; dy += ry*w; dz += rz*w;
        }
      }

      delta[i] = dx; delta[i+1] = dy; delta[i+2] = dz;
    }
    return { name: `shape_${axis}`, delta };
  });
}
